import { useMemo } from 'react';
import { useWorkspaceStore } from '@/modules/workspaces/workspace-store';
import { effectiveTerminology, renamedTermKeys } from '@/modules/terminology/types';
import { buildTermHelpers } from '@/modules/terminology/term-helpers';

export {
  buildTermHelpers,
  type AgreeFn,
  type CountedFn,
  type PlFn,
  type TermFn,
  type TermOptions,
  type WithArticleFn,
} from '@/modules/terminology/term-helpers';

export function useTerms(): ReturnType<typeof buildTermHelpers> {
  const settings = useWorkspaceStore((s) => s.settings);
  const tier = useWorkspaceStore((s) => s.currentWorkspace?.subscriptionTier ?? null);
  const isPersonal = useWorkspaceStore((s) => !!s.currentWorkspace?.isPersonal);
  return useMemo(() => {
    const { terminology, terminologyPlurals } = effectiveTerminology(settings, tier, isPersonal);
    return buildTermHelpers(terminology, terminologyPlurals, renamedTermKeys(settings, tier, isPersonal));
  }, [settings, tier, isPersonal]);
}

const DEFAULT_HELPERS = buildTermHelpers(null, {});

/**
 * Configured terms for code that runs outside React — store actions building a
 * toast, mostly. Reads the same workspace settings `useTerms` does, so a
 * workspace calling processes "SOPs" says so in its error messages too.
 */
export function storeTerms(): ReturnType<typeof buildTermHelpers> {
  const { settings, currentWorkspace } = useWorkspaceStore.getState();
  const tier = currentWorkspace?.subscriptionTier ?? null;
  const isPersonal = !!currentWorkspace?.isPersonal;
  const { terminology, terminologyPlurals } = effectiveTerminology(settings, tier, isPersonal);
  return buildTermHelpers(terminology, terminologyPlurals, renamedTermKeys(settings, tier, isPersonal));
}

export function useDefaultTerms(): ReturnType<typeof buildTermHelpers> {
  return DEFAULT_HELPERS;
}
